// src/components/CashManagement/ExpenseModal.jsx

import React, { useState, useEffect } from 'react';
import { getLoggedInUserId } from '../../utils/apiHelper';

const ExpenseModal = ({ isOpen, onClose, onSave, selectedInventory, existingData }) => {
  const [isSaving, setIsSaving] = useState(false);
  const userId = getLoggedInUserId();
  
  const [formData, setFormData] = useState({
    inventoryId: '',
    type: 'FUEL',
    reason: '',
    amount: ''
  });

  const [expenseList, setExpenseList] = useState([]);

  // Load existing draft data when modal opens
  useEffect(() => {
    if (isOpen && existingData) {
      console.log('📋 Loading existing draft expenses:', existingData);
      setExpenseList(existingData);
    }
  }, [isOpen, existingData]);

  // Sync selected inventory from parent
  useEffect(() => {
    if (selectedInventory && selectedInventory !== 'ALL') {
      setFormData(prev => ({
        ...prev,
        inventoryId: selectedInventory
      }));
    }
  }, [selectedInventory]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleAddExpense = (e) => {
    e.preventDefault();

    if (!formData.inventoryId || !formData.type || !formData.reason || !formData.amount) {
      alert('Please fill all required fields');
      return;
    }

    const newExpense = {
      id: Date.now(), // Temporary ID for frontend tracking
      type: formData.type,
      reason: formData.reason,
      amount: parseInt(formData.amount),
      inventoryId: parseInt(formData.inventoryId),
      salePersonId: userId,
      createdBy: userId
    };

    setExpenseList(prev => [...prev, newExpense]);
    
    // Reset form but keep inventory
    setFormData({
      inventoryId: formData.inventoryId,
      type: 'FUEL',
      reason: '',
      amount: ''
    });
  };

  const handleDeleteExpense = (id) => {
    setExpenseList(prev => prev.filter(expense => expense.id !== id));
  };

  const handleSaveDraft = () => {
    if (expenseList.length === 0) {
      alert('Please add at least one expense');
      return;
    }

    console.log('📝 Saving draft expenses:', expenseList);
    
    // Pass draft to parent component (similar to CollectionModal pattern)
    onSave(expenseList);
    
    // Don't close modal or reset - let parent handle it
  };

  const handleReset = () => {
    setFormData({
      inventoryId: selectedInventory !== 'ALL' ? selectedInventory : '',
      type: 'FUEL',
      reason: '',
      amount: ''
    });
    setExpenseList([]);
  };

  const handleClose = () => {
    if (isSaving) return;
    
    // If there are unsaved expenses, warn user
    if (expenseList.length > 0 && !existingData) {
      const confirmClose = window.confirm(
        'You have unsaved expenses. Are you sure you want to close?'
      );
      if (!confirmClose) return;
    }
    
    handleReset();
    onClose();
  };

  const getTotalAmount = () => {
    return expenseList.reduce((sum, expense) => sum + expense.amount, 0);
  };

  const getExpenseTypeLabel = (type) => {
    const labels = {
      'FUEL': 'Fuel',
      'LUNCH': 'Lunch',
      'REPAIR': 'Repair',
      'OFFICE_SUPPLIES': 'Office Supplies',
      'TRANSPORT': 'Transport',
      'OTHER': 'Other'
    };
    return labels[type] || type;
  };

  if (!isOpen) return null;

  return (
    <div className="cash-modal-overlay" onClick={handleClose}>
      <div className="cash-modal-content expense-modal-large" onClick={(e) => e.stopPropagation()}>
        <div className="cash-modal-header">
          <h2>Add Expenses</h2>
          <button className="cash-close-btn" onClick={handleClose} disabled={isSaving}>
            &times;
          </button>
        </div>

        <div className="cash-modal-body">
          {/* Expense Form */}
          <form onSubmit={handleAddExpense}>
            <div className="form-row">
              <div className="form-group">
                <label>Inventory *</label>
                <input
                  type="text"
                  value={formData.inventoryId}
                  disabled
                  className="locked-field"
                />
              </div>

              <div className="form-group">
                <label>Expense Type *</label>
                <select
                  name="type"
                  value={formData.type}
                  onChange={handleChange}
                  required
                  disabled={isSaving}
                >
                  <option value="FUEL">Fuel</option>
                  <option value="LUNCH">Lunch</option>
                  <option value="REPAIR">Repair</option>
                  <option value="OFFICE_SUPPLIES">Office Supplies</option>
                  <option value="TRANSPORT">Transport</option>
                  <option value="OTHER">Other</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label>Reason *</label>
                <input
                  type="text"
                  name="reason"
                  value={formData.reason}
                  onChange={handleChange}
                  placeholder="Vehicle refueling for sales trip"
                  required
                  disabled={isSaving}
                />
              </div>

              <div className="form-group">
                <label>Amount *</label>
                <input
                  type="number"
                  name="amount"
                  value={formData.amount}
                  onChange={handleChange}
                  placeholder="5000"
                  step="1"
                  min="0"
                  required
                  disabled={isSaving}
                />
              </div>
            </div>

            <div className="form-actions">
              <button 
                type="submit" 
                className="cash-btn cash-btn-add"
                disabled={isSaving}
              >
                + Add to List
              </button>
            </div>
          </form>

          {/* Expenses List */}
          {expenseList.length > 0 && (
            <div className="expenses-list-container">
              <div className="expenses-list-header">
                <h3>Added Expenses ({expenseList.length})</h3>
                <div className="total-amount">
                  Total: Rs. {getTotalAmount().toLocaleString()}
                </div>
              </div>
              
              <div className="expenses-list">
                <table className="expenses-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Type</th>
                      <th>operational</th>
                      <th>transport</th>
                      <th>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {expenseList.map((expense, index) => (
                      <tr key={expense.id}>
                        <td>{index + 1}</td>
                        <td>
                          <span className="expense-type-badge">
                            {getExpenseTypeLabel(expense.type)}
                          </span>
                        </td>
                        <td>{expense.reason}</td>
                        <td className="amount-cell">Rs. {expense.amount.toLocaleString()}</td>
                        <td>
                          <button
                            className="delete-btn"
                            onClick={() => handleDeleteExpense(expense.id)}
                            disabled={isSaving}
                            title="Delete expense"
                          >
                            🗑️
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        <div className="cash-modal-footer">
          <button 
            className="cash-btn cash-btn-cancel" 
            onClick={handleClose} 
            disabled={isSaving}
          >
            Cancel
          </button>
          <button 
            className="cash-btn cash-btn-save" 
            onClick={handleSaveDraft} 
            disabled={isSaving || expenseList.length === 0}
          >
            {isSaving ? 'Saving...' : `Save Draft (${expenseList.length})`}
          </button>
        </div>
      </div>

      <style jsx>{`
        .expense-modal-large {
          max-width: 800px;
          max-height: 90vh;
          overflow-y: auto;
        }

        .form-actions {
          margin-top: 15px;
          display: flex;
          justify-content: flex-end;
        }

        .cash-btn-add {
          background: #10b981;
          color: white;
          padding: 8px 16px;
          border: none;
          border-radius: 6px;
          cursor: pointer;
          font-size: 14px;
          font-weight: 500;
        }

        .cash-btn-add:hover:not(:disabled) {
          background: #059669;
        }

        .cash-btn-add:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .expenses-list-container {
          margin-top: 30px;
          border-top: 2px solid #e5e7eb;
          padding-top: 20px;
        }

        .expenses-list-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 15px;
        }

        .expenses-list-header h3 {
          margin: 0;
          font-size: 18px;
          color: #1f2937;
        }

        .total-amount {
          font-size: 18px;
          font-weight: 600;
          color: #059669;
        }

        .expenses-list {
          overflow-x: auto;
        }

        .expenses-table {
          width: 100%;
          border-collapse: collapse;
          background: white;
          border-radius: 8px;
          overflow: hidden;
          box-shadow: 0 1px 3px rgba(0,0,0,0.1);
        }

        .expenses-table thead {
          background: #f3f4f6;
        }

        .expenses-table th {
          padding: 12px;
          text-align: left;
          font-weight: 600;
          font-size: 13px;
          color: #6b7280;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .expenses-table td {
          padding: 12px;
          border-top: 1px solid #e5e7eb;
          font-size: 14px;
          color: #374151;
        }

        .expenses-table tbody tr:hover {
          background: #f9fafb;
        }

        .expense-type-badge {
          display: inline-block;
          padding: 4px 10px;
          background: #dbeafe;
          color: #1e40af;
          border-radius: 12px;
          font-size: 12px;
          font-weight: 500;
        }

        .amount-cell {
          font-weight: 600;
          color: #059669;
        }

        .delete-btn {
          background: none;
          border: none;
          cursor: pointer;
          font-size: 18px;
          padding: 4px 8px;
          border-radius: 4px;
          transition: background 0.2s;
        }

        .delete-btn:hover:not(:disabled) {
          background: #fee2e2;
        }

        .delete-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .locked-field {
          background: #f3f4f6;
          cursor: not-allowed;
        }
      `}</style>
    </div>
  );
};

export default ExpenseModal;